import type { Edge, Node } from 'react-vis-graph-wrapper';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { differenceInMinutes, formatDistance, isFuture } from 'date-fns';

import { setNetworkDetails } from 'src/app/reducers/room/room.reducer';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { addSocketListener, emitSocketEvent, removeSocketListener } from 'src/common/sockets';
import { NetworkInstance } from 'src/common/interfaces/networks/instances';
import { useUpdateNetworkMutation } from 'src/common/slices/networks-instances';
import { NetworkNode } from 'src/common/interfaces/networks/diagrams';
import { nodeLabelReplace } from 'src/features/room/helpers';

import { RoomTask } from '../interfaces/tasks';

const applyTaskChangesToNode = (node: NetworkNode, tasks?: RoomTask[]) => {
  const { taskChanges } = node;
  let { image } = node;
  let label = nodeLabelReplace(node.label);
  for (const taskChange of taskChanges) {
    const matchedTask = tasks?.find((task) => task.taskNo === taskChange.task);
    const matchedQuestion = matchedTask?.questions.find((question) => question.questionNo === taskChange.question);
    if (matchedQuestion?.progress?.correct) {
      image = taskChange.image;
      label = nodeLabelReplace(taskChange.label);
    }
  }

  return { image, label };
};

const convertToVisNode = (node: NetworkNode, answers?: RoomTask[]) => {
  const { image, label } = applyTaskChangesToNode(node, answers);
  return {
    ...node,
    image,
    label,
    level: node.level ?? 0,
  } as Node;
};

interface NetworkInstanceSocketConfig {
  joinEvent: string;
  consumeEvent: string;
}

export const userNetworkInstanceSocketConfig: NetworkInstanceSocketConfig = {
  joinEvent: 'network-join',
  consumeEvent: 'network-instance',
};
export const adminNetworkInstanceSocketConfig: NetworkInstanceSocketConfig = {
  joinEvent: 'admin-network-join',
  consumeEvent: 'admin-network-instance',
};

export const useNetworkInstance = ({
  tasks,
  restNetworkInstance,
  eventSubscription,
}: {
  tasks: RoomTask[];
  restNetworkInstance?: NetworkInstance;
  eventSubscription: NetworkInstanceSocketConfig;
}) => {
  const dispatch = useDispatch();
  const [networkInstance, setNetworkInstance] = useState<NetworkInstance>();

  useEffect(() => {
    if (restNetworkInstance) {
      setNetworkInstance(restNetworkInstance);
    }
  }, [restNetworkInstance]);

  useEffect(() => {
    dispatch(setNetworkDetails(networkInstance));
  }, [dispatch, networkInstance]);

  const [updateNetwork, { isLoading: isNetworkUpdating }] = useUpdateNetworkMutation();

  const connectToNetwork = useCallback(() => {
    if (networkInstance?._id) {
      emitSocketEvent(eventSubscription.joinEvent, networkInstance._id);
    }
  }, [eventSubscription, networkInstance?._id]);

  const { networkDiagramNodes, networkDiagramEdges } = useMemo(() => {
    if (networkInstance?.networkDiagram) {
      const nodes = networkInstance.networkDiagram.nodes.map((node) => convertToVisNode(node, tasks));
      const edges = networkInstance.networkDiagram.connections.map((c, i) => ({ ...c, id: i } as Edge));
      return { networkDiagramNodes: nodes, networkDiagramEdges: edges };
    }
    return { networkDiagramNodes: null, networkDiagramEdges: null };
  }, [networkInstance?.networkDiagram, tasks]);

  const timeUntilNetworkPause = useMemo(() => {
    if (networkInstance?.pauseTime) {
      const pause = new Date(networkInstance?.pauseTime);
      if (isFuture(pause)) {
        return pause;
      }
      return null;
    }
    return null;
  }, [networkInstance?.pauseTime]);

  const extendNetworkPauseTime = useCallback(
    (newPauseTime: Date) => {
      if (!networkInstance?._id || !networkInstance.pauseTime) return;
      const timeUntilPause = differenceInMinutes(new Date(networkInstance.pauseTime), new Date());

      if (timeUntilPause <= 60) {
        updateNetwork({
          networkInstanceId: networkInstance._id,
          pauseTime: newPauseTime,
        })
          .then(() => NotificationDispatch('success', 'Success! Network expire time updated.'))
          .catch(() =>
            NotificationDispatch('error', 'Uh-oh, an error occurred. Please try again, or contact support.'),
          );
      } else {
        NotificationDispatch('error', `Uh-oh! You can only increase the expiry time for a maximum of two hours`);
      }
    },
    [networkInstance?._id, networkInstance?.pauseTime, updateNetwork],
  );

  const handleResetRequest = useCallback(() => {
    if (networkInstance?._id) {
      updateNetwork({
        networkInstanceId: networkInstance._id,
        reset: true,
      })
        .then((resp) => {
          if ('error' in resp && resp.error && isQueryErrorWithMessage(resp.error)) {
            NotificationDispatch('error', resp.error.data.message);
          } else {
            NotificationDispatch('success', 'Your reset has been counted!');
          }
        })
        .catch(() => NotificationDispatch('error', 'Uh-oh, an error occurred, please try again or contact support.'));
    }
  }, [networkInstance?._id, updateNetwork]);

  useEffect(() => {
    connectToNetwork();
  }, [connectToNetwork]);

  useEffect(() => {
    addSocketListener(
      eventSubscription.consumeEvent,
      (data: { event: 'refresh' | 'reset' | 'error'; networkInstance: NetworkInstance; message?: string }) => {
        if (data.event === 'reset') {
          NotificationDispatch('success', 'Enough resets requested to begin network reset.');
        }
        if (data.event === 'error' && data.message) {
          NotificationDispatch('error', data.message);
        }
        setNetworkInstance(data.networkInstance);
      },
    );

    return () => {
      removeSocketListener(eventSubscription.consumeEvent);
    };
  }, [eventSubscription.consumeEvent]);

  const [timeSinceStart, setTimeSinceStart] = useState<string>();

  useEffect(() => {
    const updateMinutes = () => {
      if (networkInstance?.startTime) {
        setTimeSinceStart(formatDistance(new Date(networkInstance.startTime), new Date()));
      }
    };
    updateMinutes();
    const interval = setInterval(updateMinutes, 60_000);
    return () => clearInterval(interval);
  }, [networkInstance?.startTime]);

  return {
    ...networkInstance,
    extendNetworkPauseTime,
    handleResetRequest,
    isNetworkUpdating,
    timeSinceStart,
    networkDiagramNodes,
    networkDiagramEdges,
    timeUntilNetworkPause,
  };
};
